;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:native-teams-frontend":true});}catch(e){}}();import type { SelectOption } from '@workspace/ui/components/custom/select';
import type { WalletOption } from '@workspace/ui/components/custom/wallet-combobox';
import type { Expense, ExpenseCategoryOption, ExpenseFilters } from '@workspace/ui/lib';
import type { Currency } from '@workspace/ui/types';
import { format, startOfMonth, subMonths } from 'date-fns';
import {
  CarFront,
  Coffee,
  Fuel,
  GraduationCap,
  Hotel,
  LayoutGrid,
  type LucideIcon,
  Mail,
  Megaphone,
  Monitor,
  Pencil,
  Phone,
  Presentation,
  Users,
} from 'lucide-react';
import type {
  ExpenseApprovedTotalsData,
  ExpenseApprovedTotalsItem,
  ExpenseCategory,
  ExpenseCategoryBreakdownData,
  ExpenseCategoryBreakdownPeriod,
  ExpensePayerWallet,
  ExpenseRowItem,
  ExpenseRowStatus,
} from '@/types/expense';

const PAYER_WALLET_FLAG_BASE_URL = 'https://i.nativeteams.com/assets/app/flags_code';

interface ToPayerWalletOptionOptions {
  /** Bulk approval must not use the per-expense flag to disable a wallet. */
  checkSufficientFunds?: boolean;
}

/** Maps a payer wallet to a combobox option. Hides the balance when the API returns null. */
export function toPayerWalletOption(
  wallet: ExpensePayerWallet,
  currencies: Currency[] | undefined,
  options?: ToPayerWalletOptionOptions
): WalletOption {
  const currency = currencies?.find((item) => item.code === wallet.currency_code);
  const flagCode = currency?.flag_code ?? wallet.currency_code.toLowerCase();
  const checkSufficientFunds = options?.checkSufficientFunds ?? true;

  return {
    currencyId: wallet.wallet_currency_id,
    currencyCode: wallet.currency_code,
    balance: wallet.available_balance,
    flagUrl: `${PAYER_WALLET_FLAG_BASE_URL}/${flagCode}.svg`,
    disabled: !wallet.is_active || (checkSufficientFunds && wallet.has_sufficient_funds === false),
  };
}

const CATEGORY_MAP: Record<string, ExpenseCategory> = {
  travel_fuel: 'fuel',
  travel_hotels_flights: 'hotels_flights',
  travel_transportation: 'transport',
  meals: 'meals',
  team_building_entertainment: 'team_building',
  office_supplies_equipment: 'office',
  communication: 'communication',
  postage_courier: 'postage',
  marketing_advertising: 'marketing',
  conferences_events: 'conferences',
  it_costs_subscriptions: 'subscription',
  professional_development: 'training',
  other: 'other',
};

export function mapApiCategory(value: string | null | undefined): ExpenseCategory {
  if (!value) return 'other';
  return CATEGORY_MAP[value] ?? 'other';
}

/**
 * Surfaces "Action needed" to whoever must act next:
 * - Manager: pending expenses (approve/reject the request) or prepaid receipts the
 *   employee has uploaded (`submitted`).
 * - Employee: prepaid receipt upload / re-upload (`pending_receipt`, `receipt_declined`).
 */
export function isExpenseActionNeeded(expense: Expense, isEmployer = false): boolean {
  if (isEmployer) {
    if (expense.status === 'pending') return true;
    return (
      expense.expense_type === 'prepaid_expense' && expense.prepaid_receipt_status === 'submitted'
    );
  }

  return (
    expense.expense_type === 'prepaid_expense' &&
    (expense.prepaid_receipt_status === 'pending_receipt' ||
      expense.prepaid_receipt_status === 'receipt_declined')
  );
}

export function mapApiStatus(expense: Expense, isEmployer = false): ExpenseRowStatus {
  if (isExpenseActionNeeded(expense, isEmployer)) {
    return 'action_needed';
  }

  if (
    isEmployer &&
    expense.expense_type === 'prepaid_expense' &&
    (expense.prepaid_receipt_status === 'pending_receipt' ||
      expense.prepaid_receipt_status === 'receipt_declined')
  ) {
    return 'pending_receipt';
  }

  switch (expense.status) {
    case 'approved':
    case 'paid':
      return 'approved';
    case 'declined':
      return 'rejected';
    case 'cancelled':
      return 'cancelled';
    default:
      return 'pending';
  }
}

export function getFileExtension(name: string): string {
  const clean = name.split('?')[0]?.split('#')[0] ?? '';
  const idx = clean.lastIndexOf('.');
  return idx >= 0 ? clean.slice(idx + 1).toLowerCase() : '';
}

export function isPdfFileName(name: string): boolean {
  return getFileExtension(name) === 'pdf';
}

export const EXPENSE_RECEIPT_IMAGE_EXTENSIONS = [
  'png',
  'jpg',
  'jpeg',
  'gif',
  'webp',
  'bmp',
  'svg',
  'heic',
  'heif',
] as const;

export function isExpenseReceiptImageFileName(name: string): boolean {
  return (EXPENSE_RECEIPT_IMAGE_EXTENSIONS as readonly string[]).includes(getFileExtension(name));
}

export function isBlobUrl(url: string): boolean {
  return url.startsWith('blob:');
}

/** Remote PDFs need a proxied blob so the browser can preview instead of download. */
export function expenseReceiptNeedsProxiedPreview(name: string, fileUrl: string): boolean {
  return isPdfFileName(name) && !isBlobUrl(fileUrl);
}

export function mapExpenseToRow(expense: Expense, isEmployer = false): ExpenseRowItem {
  return {
    id: String(expense.id),
    title: expense.description || expense.merchant_name || '—',
    amount: Number.parseFloat(expense.amount) || 0,
    currency: expense.currency,
    category: mapApiCategory(expense.category),
    status: mapApiStatus(expense, isEmployer),
    requestDate: expense.request_date ?? '',
    reimbursementMethod: expense.reimbursement_method,
  };
}

export interface ExpenseReviewState {
  isPrepaid: boolean;
  isPending: boolean;
  isApproved: boolean;
  isDeclined: boolean;
  isCancelled: boolean;
  isPendingReceipt: boolean;
  /** Manager must act: pending request approval, or prepaid receipts the employee submitted. */
  isActionable: boolean;
  /** Receipt has been submitted by employee and is awaiting employer review. */
  isReceiptSubmitted: boolean;
  /** Employer approved the submitted receipts. */
  isReceiptApproved: boolean;
  /** Employer declined the submitted receipts (employee can re-submit). */
  isReceiptDeclined: boolean;
}

export function getExpenseReviewState(expense: Expense): ExpenseReviewState {
  const isPrepaid = expense.expense_type === 'prepaid_expense';
  const isPending = expense.status === 'pending';
  const isApproved = expense.status === 'approved' || expense.status === 'paid';
  const isDeclined = expense.status === 'declined';
  const isCancelled = expense.status === 'cancelled';
  const isPendingReceipt = isPrepaid && expense.prepaid_receipt_status === 'pending_receipt';
  const isReceiptSubmitted = isPrepaid && expense.prepaid_receipt_status === 'submitted';
  const isReceiptApproved = isPrepaid && expense.prepaid_receipt_status === 'receipt_approved';
  const isReceiptDeclined = isPrepaid && expense.prepaid_receipt_status === 'receipt_declined';

  return {
    isPrepaid,
    isPending,
    isApproved,
    isDeclined,
    isCancelled,
    isPendingReceipt,
    isActionable: isExpenseActionNeeded(expense, true),
    isReceiptSubmitted,
    isReceiptApproved,
    isReceiptDeclined,
  };
}

export type ReceiptReconciliationState = 'matches' | 'overspent' | 'underspent';

export interface ReceiptReconciliation {
  prepaidAmount: number;
  actualAmount: number;
  difference: number;
  state: ReceiptReconciliationState;
}

/**
 * Computes the reconciliation between what the employer prepaid and what the employee
 * actually spent (submitted via receipt). Only meaningful when receipt_amount is set.
 */
export function getReceiptReconciliation(expense: Expense): ReceiptReconciliation {
  const prepaidAmount = Number(expense.approved_amount ?? expense.amount ?? 0);
  const actualAmount = Number(expense.receipt_amount ?? 0);
  const difference = actualAmount - prepaidAmount;

  let state: ReceiptReconciliationState = 'matches';
  if (difference > 0) state = 'overspent';
  else if (difference < 0) state = 'underspent';

  return { prepaidAmount, actualAmount, difference, state };
}

export const CATEGORY_ICON: Record<ExpenseCategory, LucideIcon> = {
  meals: Coffee,
  transport: CarFront,
  office: Pencil,
  subscription: Monitor,
  other: LayoutGrid,
  fuel: Fuel,
  hotels_flights: Hotel,
  team_building: Users,
  communication: Phone,
  postage: Mail,
  marketing: Megaphone,
  conferences: Presentation,
  training: GraduationCap,
};

export interface ExpenseScopeContext {
  isManager: boolean;
  isTeamLead: boolean;
  isEmployer: boolean;
  isGlobalAdmin: boolean;
  view: 'personal' | 'team';
  teamId: number | undefined;
  userId: number | undefined;
}

/**
 * Employer prepaid "Action needed": pending requests OR receipts awaiting review (`submitted`).
 * `status[]` + `prepaid_receipt_status[]` are ANDed, so this uses a status union plus
 * `exclude_prepaid_receipt_status` to keep `submitted` approved rows without pulling in
 * pending_receipt / receipt_declined / receipt_approved.
 */
export function getManagerPrepaidActionNeededFilters(): Pick<
  ExpenseFilters,
  'status' | 'exclude_prepaid_receipt_status'
> {
  return {
    status: ['pending', 'approved', 'paid'],
    exclude_prepaid_receipt_status: ['pending_receipt', 'receipt_declined', 'receipt_approved'],
  };
}

/**
 * Employee prepaid "Action needed" / "Pending receipts": receipt upload or re-upload.
 * No `status[]` — those rows are typically already approved.
 */
export function getEmployeePrepaidActionNeededFilters(): Pick<
  ExpenseFilters,
  'prepaid_receipt_status'
> {
  return { prepaid_receipt_status: ['pending_receipt', 'receipt_declined'] };
}

/**
 * Dashboard / actionable-list filters for the viewer's current role.
 * Employee prepaid action-needed is a separate query (`getEmployeePrepaidActionNeededFilters`)
 * because `status[]` + `prepaid_receipt_status[]` are ANDed.
 */
export function getActionableExpenseFilters(
  expenseType: ExpenseFilters['expense_type'],
  isManager: boolean
): Pick<ExpenseFilters, 'status' | 'prepaid_receipt_status' | 'exclude_prepaid_receipt_status'> {
  if (expenseType === 'reimbursement') return { status: ['pending'] };

  if (isManager) return getManagerPrepaidActionNeededFilters();

  return { status: ['pending'] };
}

/**
 * Receipt statuses that should not appear on the Approved tab for this viewer.
 * Reimbursements (`prepaid_receipt_status` null) are never dropped.
 */
export function getActionNeededReceiptStatusesToExclude(
  isManager: boolean
): NonNullable<ExpenseFilters['exclude_prepaid_receipt_status']> {
  return isManager
    ? ['submitted', 'pending_receipt', 'receipt_declined']
    : ['pending_receipt', 'receipt_declined'];
}

/**
 * Returns the API filter fields that scope expense queries to the correct set of data
 * based on the current user's role and selected view.
 *
 * - Employer                → no extra filters (org-wide, backend handles it)
 * - Team lead, team view    → team_id + view_self_inputs: false
 * - Team lead, personal     → team_id + view_self_inputs: true + user_id (own expenses only)
 * - Global admin, team view → view_self_inputs: false (org-wide, no team_id)
 * - Global admin, personal  → view_self_inputs: true + user_id (own expenses only)
 * - Regular employee        → no extra filters (backend scopes by auth token)
 */
export function buildExpenseScopeFilters(
  ctx: ExpenseScopeContext
): Pick<ExpenseFilters, 'team_id' | 'view_self_inputs' | 'user_id'> {
  const { isEmployer, isGlobalAdmin, isTeamLead, view, teamId, userId } = ctx;

  if (isEmployer) return {};

  if (isTeamLead || isGlobalAdmin) {
    const isTeamView = view === 'team';
    return {
      ...(isTeamLead && teamId ? { team_id: teamId } : {}),
      view_self_inputs: !isTeamView,
      ...(!isTeamView && userId ? { user_id: userId } : {}),
    };
  }

  return {};
}

export const CATEGORY_COLOR: Record<ExpenseCategory, string> = {
  meals: 'text-pink-400',
  transport: 'text-teal-700',
  office: 'text-orange-400',
  subscription: 'text-red-400',
  other: 'text-neutral-200 dark:text-neutral-100',
  fuel: 'text-yellow-500',
  hotels_flights: 'text-blue-400',
  team_building: 'text-green-500',
  communication: 'text-purple-400',
  postage: 'text-indigo-400',
  marketing: 'text-rose-400',
  conferences: 'text-cyan-500',
  training: 'text-amber-500',
};

export interface ExpensePeriod {
  value: ExpenseCategoryBreakdownPeriod;
  label: string;
  months: number;
}

export const EXPENSE_PERIODS: ExpensePeriod[] = [
  { value: 'last_1_month', label: 'This month', months: 1 },
  { value: 'last_3_months', label: 'Last 3 months', months: 3 },
  { value: 'last_6_months', label: 'Last 6 months', months: 6 },
  { value: 'last_12_months', label: 'Last 12 months', months: 12 },
];

export function getExpensePeriodDateRange(
  period: ExpenseCategoryBreakdownPeriod,
  today: Date = new Date()
): { from: string; to: string } {
  const months = EXPENSE_PERIODS.find((option) => option.value === period)?.months ?? 1;
  const from = subMonths(startOfMonth(today), months - 1);

  return {
    from: format(from, 'yyyy-MM-dd'),
    to: format(today, 'yyyy-MM-dd'),
  };
}

export function getExpenseCurrencyListLabel(currency: { name: string; code: string }): string {
  return `${currency.name} (${currency.code})`;
}

export function getExpenseCurrencyFlagUrl(flagCode: string): string {
  return `https://i.nativeteams.com/assets/app/flags_code/${flagCode}.svg`;
}

/** Maps expense API currencies to shared Select options for chart header pickers. */
export function buildExpenseCurrencySelectOptions(currencies: Currency[]): SelectOption[] {
  return currencies.map((currency) => ({
    name: getExpenseCurrencyListLabel(currency),
    triggerName: currency.code,
    value: String(currency.id),
    image: {
      path: getExpenseCurrencyFlagUrl(currency.flag_code),
      alt: currency.code,
    },
  }));
}

export const EXPENSE_CHART_CURRENCY_SELECT_CLASSNAME = 'shrink-0';
export const EXPENSE_CHART_CURRENCY_SELECT_TRIGGER_CLASSNAME = 'min-w-[124px]';
export const EXPENSE_CHART_CURRENCY_SELECT_CONTENT_CLASSNAME =
  'w-max min-w-(--radix-select-trigger-width) max-w-[calc(100vw-2rem)] [&_[data-slot=select-item]]:whitespace-nowrap';
export const EXPENSE_CHART_CURRENCY_SELECT_SCROLL_AREA_CLASSNAME = 'w-full pl-0 pr-6';

export interface CategoryBreakdownViewItem {
  id: string;
  name: string;
  amount: number;
  currency: string;
  category: ExpenseCategory;
}

/**
 * Maps the raw category-breakdown API response into renderable view items:
 * resolves the local icon/color key and translated label, drops zero-spend
 * categories, and sorts by amount descending.
 */
export function buildBreakdownViewItems(
  data: ExpenseCategoryBreakdownData | undefined,
  categoryOptions: ExpenseCategoryOption[]
): CategoryBreakdownViewItem[] {
  if (!data) return [];

  const nameByValue = new Map(categoryOptions.map((option) => [option.value, option.name]));
  const currency = data.currency.code ?? '';

  return data.items
    .filter((item) => item.amount > 0)
    .map((item) => ({
      id: item.category,
      name: nameByValue.get(item.category) ?? item.category,
      amount: item.amount,
      currency,
      category: mapApiCategory(item.category),
    }))
    .sort((a, b) => b.amount - a.amount);
}

const MONTH_LABELS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

export interface MonthlyPaidChartItem {
  month: string;
  amount: number;
}

/**
 * Maps the raw approved-totals API response (YYYY-MM buckets) into chart-ready items
 * with short month labels ("Jan", "Feb", …). Safe against timezone drift.
 */
export function buildMonthlyPaidItems(
  data: ExpenseApprovedTotalsData | undefined
): MonthlyPaidChartItem[] {
  if (!data) return [];

  return data.items.map((item: ExpenseApprovedTotalsItem) => {
    // "2025-02" → month index 1 → "Feb"
    const monthIndex = parseInt(item.month.slice(5, 7), 10) - 1;
    return {
      month: MONTH_LABELS[monthIndex] ?? item.month,
      amount: item.amount,
    };
  });
}
